// Browser-owned chip deletion: the search div is a contentEditable whose chips
// are atomic contenteditable="false" spans. React owns none of these nodes, so
// we manipulate them directly. Two-step delete (arm → remove) is implemented in
// NavBar's keydown handler using these pure helpers.

export const ARMED_CLASS = 'chip-armed';

function isChip(node: Node | null): node is HTMLElement {
  return !!node && node.nodeType === Node.ELEMENT_NODE &&
    (node as HTMLElement).hasAttribute('data-chip');
}

// The node on `side` of the caret. For a caret inside a text node we treat it
// as adjacent only at the very edge (offset 0 for backward, end for forward),
// then walk up through inline wrappers (e.g. <span class="mx-1">) to find the
// sibling on that side — stopping at `boundary` so we never escape the editable.
// For a caret directly in an element container we look at the child at the
// boundary index.
function nodeOnSide(
  container: Node,
  offset: number,
  direction: 'backward' | 'forward',
  boundary: HTMLElement
): Node | null {
  if (container.nodeType === Node.TEXT_NODE) {
    const len = container.textContent?.length ?? 0;
    const atEdge = direction === 'backward' ? offset === 0 : offset === len;
    if (!atEdge) return null;
    let node: Node | null = container;
    while (node && node !== boundary) {
      const sibling = direction === 'backward' ? node.previousSibling : node.nextSibling;
      if (sibling) return sibling;
      node = node.parentNode;
    }
    return null;
  }
  // Element/document-fragment container: children indexed by offset.
  const children = container.childNodes;
  if (direction === 'backward') {
    return offset > 0 ? children[offset - 1] : null;
  }
  return offset < children.length ? children[offset] : null;
}

export function chipAdjacentToCaret(
  div: HTMLElement,
  direction: 'backward' | 'forward'
): HTMLElement | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (!div.contains(range.startContainer)) return null;
  const candidate = nodeOnSide(range.startContainer, range.startOffset, direction, div);
  return isChip(candidate) ? candidate : null;
}

export function armChip(chip: HTMLElement): void {
  chip.classList.add(ARMED_CLASS);
}

export function disarmChip(chip: HTMLElement | null): void {
  chip?.classList.remove(ARMED_CLASS);
}

export function removeChip(chip: HTMLElement): void {
  chip.remove();
}
